import { FormEvent, ReactNode, useState } from "react";
import { MapPin, ShieldAlert, Users, Workflow } from "lucide-react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { usePinnedArtifactMutations } from "./usePinnedArtifacts";

interface PinArtifactDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  conversationId: string;
  artifactName: string;
  defaultDisplayName: string;
}

// Names the card and confirms the pin. The workflow behind it is derived
// server-side from the artifact's lineage, so there is nothing to choose here —
// the dialog exists to give the card a label and to say plainly what pinning
// does: who sees it, that it keeps how the artifact was made, and whose data
// access a refresh uses. None of that is obvious from the button.
//
// Mounted only while open, so the name resets to the artifact's own summary
// without an effect to reset it.
export const PinArtifactDialog = ({
  open,
  onClose,
  projectId,
  conversationId,
  artifactName,
  defaultDisplayName,
}: PinArtifactDialogProps) => {
  const [displayName, setDisplayName] = useState(defaultDisplayName);
  const { pin, isMutating } = usePinnedArtifactMutations(projectId);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const created = await pin({
      conversation_id: conversationId,
      artifact_name: artifactName,
      display_name: displayName.trim() || defaultDisplayName,
    });
    if (created) onClose();
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title="Pin to project"
      description="Keep this artifact on the project, ready to refresh."
      primaryLabel="Pin to project"
      primaryPendingLabel="Pinning…"
      primaryDisabled={displayName.trim().length === 0}
      isPending={isMutating}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="pin-display-name">Name</Label>
          <Input
            id="pin-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={500}
            placeholder={defaultDisplayName}
          />
        </div>
        <ul className="flex flex-col gap-3 text-sm">
          <Consequence icon={<Users />}>
            <strong>Visible to everyone in the project.</strong> Only project owners can unpin.
          </Consequence>
          <Consequence icon={<Workflow />}>
            <strong>Rebuildable:</strong> pinning keeps the artifact&apos;s lineage steps, so it can
            be refreshed from its sources.
          </Consequence>
          <Consequence icon={<ShieldAlert />}>
            <strong>Refreshing uses the refresher&apos;s data access</strong>, not yours.
          </Consequence>
          <Consequence icon={<MapPin />}>
            Appears on the project&apos;s <strong>Artifacts</strong> page.
          </Consequence>
        </ul>
      </div>
    </FormDialog>
  );
};

const Consequence = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 shrink-0 text-muted-foreground [&>svg]:size-4" aria-hidden>
      {icon}
    </span>
    <span>{children}</span>
  </li>
);
